import { useTranslations } from 'next-intl';
import { useEvent, useUnit } from 'effector-react';
import { AxiosError } from 'axios';
import { closeModalEvent, openModalEvent } from '@nx-frontend/shared/ui/modal';
import { axiosClient } from '@nx-frontend/shared/api';
import { $currentOrganization } from '@nx-frontend/shared/store/current-organization';
import { webnotesThemeColors, webnotesThemeStyle } from '@nx-frontend/shared/themes/webnotes';
import { PaywallPopupReason, PopupType, Workspace } from '@nx-frontend/shared/types';
// eslint-disable-next-line no-restricted-imports
import { useOpenPaywallPopup } from '../use-open-paywall-popup';

export function useOpenCreateWorkspaceDialog() {
  const [currentOrganization] = useUnit([$currentOrganization]);
  const [openModal, closeModal] = useEvent([openModalEvent, closeModalEvent]);
  const openPaywallPopup = useOpenPaywallPopup();
  const t = useTranslations('features');

  return (noRedirect?: boolean, onSuccess?: (props: Workspace) => void) => {
    const modalTitle = t('workspace-select.create_workspace');
    const folderDefaultTitle = t('workspace-select.new_workspace');

    openModal({
      title: modalTitle,
      content: PopupType.RENAME,
      showCloseButton: true,
      closeButtonSize: 18,
      payload: {
        type: 'workspace',
        value: folderDefaultTitle,
        payloadType: PopupType.RENAME,
        themeColors: webnotesThemeColors,
        themeStyle: webnotesThemeStyle,
        onApply: async (value: string) => {
          try {
            const workspace = (
              await axiosClient.post<Workspace>(`/v2/api/workspaces/create`, {
                orgId: currentOrganization.globalId,
                title: value,
                skipLimits: false,
              })
            ).data;
            if (!noRedirect) {
              window.location.href = `/space/${workspace.globalId}/all`;
            }
            onSuccess?.(workspace);
          } catch (err) {
            const error = err as AxiosError;
            closeModal();
            if (
              error.response &&
              error.response.data &&
              'object' === typeof error.response.data &&
              'name' in error.response.data &&
              'LimitError' === error.response.data.name
            ) {
              openPaywallPopup({
                payloadType: PopupType.PAYWALL,
                mode: 'upgrade',
                title: t('paywall.title_increase_workspaces_by_upgrading'),
                reason: PaywallPopupReason.WORKSPACES_LIMIT,
                orgId: currentOrganization.globalId,
              });
            }
          }
        },
      },
    });
  };
}
